<template>
  <v-card class="pa-4" outlined>
    <div class="d-flex flex-wrap align-center justify-space-between mb-3">
      <div>
        <h3 class="black--text">{{ $t("reports.aiCredit.coopMgmtTitle") }}</h3>
        <p class="grey--text mb-0 mt-1" style="max-width: 720px">
          {{ $t("reports.aiCredit.coopMgmtHelp") }}
        </p>
      </div>
    </div>

    <div class="d-flex flex-wrap align-center justify-space-between mb-3">
      <v-text-field
        v-model="search"
        :placeholder="$t('reports.aiCredit.search')"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        hide-details
        clearable
        style="max-width: 340px"
      />
      <div class="d-flex align-center">
        <span class="grey--text mr-2">{{ rangeLabel }}</span>
        <v-btn icon :disabled="page <= 1" @click="prevPage"
          ><v-icon>mdi-chevron-left</v-icon></v-btn
        >
        <v-btn icon :disabled="!hasNextPage" @click="nextPage"
          ><v-icon>mdi-chevron-right</v-icon></v-btn
        >
      </div>
    </div>

    <v-data-table
      :headers="headers"
      :items="rows"
      :loading="loading"
      :server-items-length="total"
      hide-default-footer
      class="elevation-0"
    >
      <template v-slot:[`item.cooperative`]="{ item }">
        <div class="font-weight-medium">{{ item.cooperative }}</div>
        <div class="grey--text text-caption">{{ item.primaryUser || "-" }}</div>
      </template>
      <template v-slot:[`item.availableCredits`]="{ item }">
        {{ formatNum(item.availableCredits) }}
        {{ $t("reports.aiCredit.credits") }}
      </template>
      <template v-slot:[`item.lastTopupAt`]="{ item }">
        {{ fmtDate(item.lastTopupAt) }}
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on"
              ><v-icon>mdi-dots-vertical</v-icon></v-btn
            >
          </template>
          <v-list dense>
            <v-list-item @click="openInstant(item)">
              <v-list-item-title>{{
                $t("reports.aiCredit.addInstantCredits")
              }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="openManage(item)">
              <v-list-item-title>{{
                $t("reports.aiCredit.manageCredits")
              }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="openHistory(item)">
              <v-list-item-title>{{
                $t("reports.aiCredit.creditsHistory")
              }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>

    <add-instant-credits-dialog
      :value="instantDialog"
      :cooperative="selected"
      @input="instantDialog = $event"
      @saved="onSaved"
    />
    <manage-credits-dialog
      :value="manageDialog"
      :cooperative="selected"
      @input="manageDialog = $event"
      @saved="onSaved"
    />
    <credits-history-dialog
      :value="historyDialog"
      :cooperative="selected"
      @input="historyDialog = $event"
    />
  </v-card>
</template>

<script>
import AiCreditService from "@/_services/AiCreditService";
import creditFormat from "@/mixins/creditFormat";
import AddInstantCreditsDialog from "./AddInstantCreditsDialog.vue";
import ManageCreditsDialog from "./ManageCreditsDialog.vue";
import CreditsHistoryDialog from "./CreditsHistoryDialog.vue";

export default {
  name: "CooperativeCredits",
  mixins: [creditFormat],
  components: {
    AddInstantCreditsDialog,
    ManageCreditsDialog,
    CreditsHistoryDialog,
  },
  data() {
    return {
      rows: [],
      total: 0,
      page: 1,
      limit: 10,
      search: "",
      loading: false,
      searchTimer: null,
      selected: null,
      instantDialog: false,
      manageDialog: false,
      historyDialog: false,
    };
  },
  computed: {
    headers() {
      const t = (k) => this.$t(`reports.aiCredit.${k}`);
      return [
        { text: t("colCooperative"), value: "cooperative", sortable: false },
        { text: t("colContactEmail"), value: "contactEmail", sortable: false },
        {
          text: t("colAllocationPlan"),
          value: "allocationPlan",
          sortable: false,
        },
        {
          text: t("colCreditsAvailable"),
          value: "availableCredits",
          sortable: false,
        },
        { text: t("colLastTopup"), value: "lastTopupAt", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ];
    },
    hasNextPage() {
      return this.page * this.limit < this.total;
    },
    rangeLabel() {
      const shown = Math.min(this.page * this.limit, this.total);
      return `${shown} ${this.$t("of")} ${this.total}`;
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.fetch();
      }, 500);
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.loading = true;
      try {
        const env = await AiCreditService.listCooperatives({
          page: this.page,
          limit: this.limit,
          search: this.search || "",
        });
        const payload = (env && env.data) || {};
        this.rows = payload.cooperatives || [];
        this.total = (payload.pagination && payload.pagination.total) || 0;
      } catch (e) {
        this.$notify({
          title: this.$t("reports.aiCredit.errorTitle"),
          text: this.$t("reports.aiCredit.loadFailed"),
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page -= 1;
        this.fetch();
      }
    },
    nextPage() {
      if (this.hasNextPage) {
        this.page += 1;
        this.fetch();
      }
    },
    statusColor(status) {
      return (
        { active: "green", deactivated: "red", inProcess: "orange" }[status] ||
        "grey"
      );
    },
    openInstant(item) {
      this.selected = item;
      this.instantDialog = true;
    },
    openManage(item) {
      this.selected = item;
      this.manageDialog = true;
    },
    openHistory(item) {
      this.selected = item;
      this.historyDialog = true;
    },
    onSaved() {
      this.instantDialog = false;
      this.manageDialog = false;
      this.$notify({
        title: this.$t("reports.aiCredit.successTitle"),
        text: this.$t("reports.aiCredit.saved"),
        type: "success",
      });
      this.fetch();
    },
  },
};
</script>
